<template>
  <PageContainer class="PageContainer" :breadcrumbs="breadcrumbs" mode="wide">
    <Top
      :pokemon-single-pickups-for-latest-packs="data?.pokemonSinglePickupsForLatestPacks"
      :pokemon-single-pickup-general-purpose-products="data?.pokemonSinglePickupGeneralPurposePoducts"
      :pokemon-single-pickup-basic-energy-products="data?.pokemonSinglePickupBasicEnergyPoducts"
      :pokemon-single-pickup-s-a-r-products="data?.pokemonSinglePickupSARPoducts"
    />
  </PageContainer>
</template>

<script lang="ts">
import { useSeoMeta, definePageMeta, useHead, useRuntimeConfig } from '#imports'
import { defineNuxtComponent, useAsyncData, useNuxtApp } from '#app'
import { DITokens } from '@/di'
import type { ESPokemonSingleProduct } from '@ka-nabellinc/tcg-platform-es-schema'
import Top from '@/containers/Pokemon/Top'
import PageContainer from '@/containers/Page'

const PICKUP_PRODUCTS_LIMIT = 15

export default defineNuxtComponent({
  components: {
    Top,
    PageContainer,
  },
  async setup() {
    const { public: { APP_ENV, pokemon: { latestPacks } } } = useRuntimeConfig()
    const { $container } = useNuxtApp()

    const getPokemonSinglePickupsForLatestPacks = async () => {
      const results = await Promise.allSettled(latestPacks.map(async packInfo => {
        const { packId, readMoreLabel } = packInfo as any as { packId: number; readMoreLabel: string } // Nuxtのバグでconfigファイル内のarray型が推論できないため自前定義
        const elasticsearchGateway = $container.resolve(DITokens.ElasticsearchGateway)
        const { hits } = await elasticsearchGateway.getPokemonSinglePickupLatestPackProducts(packId, PICKUP_PRODUCTS_LIMIT)
        return {
          packId,
          readMoreLabel,
          hits
        }
      }))

      return results.filter(r => r.status === 'fulfilled').map(r => r.value)
    }

    const { data } = await useAsyncData('POKEMON_TOP', async () => {
      /**
       * ピックアップ商品の取得(エラーを許容する)
       * 
       * - 新弾カード
       * - 汎用カード
       * - 基本エネルギー
       * - スペシャルアートレア
       */
      let pokemonSinglePickupGeneralPurposePoducts: ESPokemonSingleProduct[] = []
      let pokemonSinglePickupBasicEnergyPoducts: ESPokemonSingleProduct[] = []
      let pokemonSinglePickupSARPoducts: ESPokemonSingleProduct[] = []
      let pokemonSinglePickupsForLatestPacks: Array<{ packId: number; readMoreLabel: string; hits: ESPokemonSingleProduct[] }> = []

      try {
        const elasticsearchGateway = $container.resolve(DITokens.ElasticsearchGateway);

        const [
          pickupGeneralPurposeProductsPromise,
          pickupBasicEnergyProductsPromise,
          pickupSARProductsPromise,
          pickupsForLatestPacksPromise
        ] = await Promise.allSettled([
          elasticsearchGateway.getPokemonSinglePickupGeneralPurposeProducts(PICKUP_PRODUCTS_LIMIT),
          elasticsearchGateway.getPokemonSinglePickupBasicEnergyProducts(PICKUP_PRODUCTS_LIMIT),
          elasticsearchGateway.getPokemonSinglePickupSARProducts(PICKUP_PRODUCTS_LIMIT),
          getPokemonSinglePickupsForLatestPacks()
        ])

        if (pickupsForLatestPacksPromise.status === 'fulfilled') {
          pokemonSinglePickupsForLatestPacks = pickupsForLatestPacksPromise.value
        }
        if (pickupGeneralPurposeProductsPromise.status === 'fulfilled') {
          pokemonSinglePickupGeneralPurposePoducts = pickupGeneralPurposeProductsPromise.value.hits
        }
        if (pickupBasicEnergyProductsPromise.status === 'fulfilled') {
          pokemonSinglePickupBasicEnergyPoducts = pickupBasicEnergyProductsPromise.value.hits
        }
        if (pickupSARProductsPromise.status === 'fulfilled') {
          pokemonSinglePickupSARPoducts = pickupSARProductsPromise.value.hits
        }
      } catch (e) {
        // Do nothing
      }

      return {
        pokemonSinglePickupGeneralPurposePoducts,
        pokemonSinglePickupBasicEnergyPoducts,
        pokemonSinglePickupSARPoducts,
        pokemonSinglePickupsForLatestPacks
      }
    })

    const title = '【ポケモンカード通販・販売】ならカーナベル | 最安値・新弾即入荷・即日発送！'
    const description = "ポケモンカード（ポケカ）の通販・販売ならカーナベル！人気シングルからコレクション向けカードまで豊富な在庫を状態別で販売！「最安値」「送料無料」「即日発送」で、最短翌日午前中に手に入る。"
    useSeoMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      robots: APP_ENV==='production' ? 'index,follow' :'noindex,nofollow',
      twitterSite: '@ka_nabell_pcg',
      ogSiteName: 'ポケカ販売ならカーナベル！',
      ogUrl: 'https://sell.ka-nabell.com/pokemon'
    })

    definePageMeta({
      genre: 'pokemon',
      footer: {
        isShowSocialMedia: true,
        isShowShopSeries: true,
      }
    })

    useHead({
      link: [
        {
          rel: 'canonical',
          href: 'https://sell.ka-nabell.com/pokemon'
        }
      ],
    })

    const breadcrumbs = [
      {
        title: 'トレカ販売トップ',
        link: '/',
      },
      {
        title: 'ポケカ販売トップ',
      }
    ]

    return {
      data,
      breadcrumbs,
    }
  }
})
</script>

<style scoped>
.PageContainer {
  padding-top: 0;
}
</style>
